<h2 mat-dialog-title>{{ isEditMode ? 'Edit Employee Profile' : 'Add Employee' }}</h2>

<mat-dialog-content class="profile-dialog">
  @if (loading) {
    <div class="loading"><mat-spinner diameter="40"></mat-spinner></div>
  } @else {
    <form [formGroup]="employeeForm" class="employee-form">
      <section class="photo-section">
        <div class="photo-preview">
          @if (previewUrl) {
            <img [src]="previewUrl" alt="Profile preview" />
          } @else {
            <div class="photo-placeholder"><mat-icon>person</mat-icon></div>
          }
        </div>
        <div class="photo-actions">
          <div class="meta">
            <div><strong>Employee ID</strong></div>
            <div class="muted">{{ employeeCode }}</div>
          </div>
          <input #fileInput type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" hidden (change)="onFileSelected($event)" />
          <button mat-stroked-button type="button" (click)="fileInput.click()">{{ previewUrl ? 'Replace' : 'Upload' }}</button>
          @if (previewUrl) {
            <button mat-button type="button" color="warn" (click)="deletePhoto()">Delete</button>
          }
          <p class="hint">JPG, PNG, or WEBP · max 5 MB · crop after upload</p>
          @if (photoError) { <p class="error">{{ photoError }}</p> }
        </div>
      </section>

      @if (showCropper) {
        <section class="cropper">
          <h3>Crop profile picture</h3>
          <p class="hint">Adjust the square crop, then apply.</p>
          <div class="crop-controls">
            <button mat-stroked-button type="button" (click)="moveCrop(-20, 0)">Left</button>
            <button mat-stroked-button type="button" (click)="moveCrop(20, 0)">Right</button>
            <button mat-stroked-button type="button" (click)="moveCrop(0, -20)">Up</button>
            <button mat-stroked-button type="button" (click)="moveCrop(0, 20)">Down</button>
            <button mat-stroked-button type="button" (click)="resizeCrop(20)">Zoom out</button>
            <button mat-stroked-button type="button" (click)="resizeCrop(-20)">Zoom in</button>
          </div>
          <div class="crop-actions">
            <button mat-button type="button" (click)="cancelCrop()">Cancel</button>
            <button mat-raised-button color="primary" type="button" (click)="applyCrop()">Apply crop</button>
          </div>
        </section>
      }

      <mat-tab-group>
        <mat-tab label="Personal">
          <div class="tab-body">
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>First name</mat-label><input matInput formControlName="firstName"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Middle name</mat-label><input matInput formControlName="middleName"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Last name</mat-label><input matInput formControlName="lastName"></mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Gender</mat-label>
                <mat-select formControlName="gender">
                  @for (g of genders; track g.value) { <mat-option [value]="g.value">{{ g.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Date of birth</mat-label><input matInput type="date" formControlName="dateOfBirth"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Marital status</mat-label>
                <mat-select formControlName="maritalStatus">
                  @for (m of maritalStatuses; track m.value) { <mat-option [value]="m.value">{{ m.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Blood group</mat-label>
                <mat-select formControlName="bloodGroup">
                  @for (b of bloodGroups; track b.value) { <mat-option [value]="b.value">{{ b.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>CNIC / National ID</mat-label><input matInput formControlName="nationalId"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Passport number</mat-label><input matInput formControlName="passportNumber"></mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Nationality</mat-label><input matInput formControlName="nationality"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Religion</mat-label><input matInput formControlName="religion"></mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Personal email</mat-label><input matInput type="email" formControlName="personalEmail"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Company email</mat-label><input matInput type="email" formControlName="companyEmail"></mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Mobile number</mat-label><input matInput formControlName="mobileNumber"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Alternate contact</mat-label><input matInput formControlName="alternateContact"></mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Emergency contact person</mat-label><input matInput formControlName="emergencyContactName"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Emergency contact number</mat-label><input matInput formControlName="emergencyContactPhone"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Relationship</mat-label><input matInput formControlName="emergencyContactRelation"></mat-form-field>
            </div>
          </div>
        </mat-tab>

        <mat-tab label="Address">
          <div class="tab-body">
            <mat-form-field appearance="outline" class="full"><mat-label>Current address</mat-label><textarea matInput rows="2" formControlName="currentAddress"></textarea></mat-form-field>
            <mat-form-field appearance="outline" class="full"><mat-label>Permanent address</mat-label><textarea matInput rows="2" formControlName="permanentAddress"></textarea></mat-form-field>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>City</mat-label><input matInput formControlName="city"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Province</mat-label><input matInput formControlName="province"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Country</mat-label><input matInput formControlName="country"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Postal code</mat-label><input matInput formControlName="postalCode"></mat-form-field>
            </div>
          </div>
        </mat-tab>

        <mat-tab label="Employment">
          <div class="tab-body">
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Joining date</mat-label><input matInput type="date" formControlName="hireDate"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Confirmation date</mat-label><input matInput type="date" formControlName="confirmationDate"></mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Department</mat-label>
                <mat-select formControlName="departmentId">
                  <mat-option value="">—</mat-option>
                  @for (d of departments; track d.id) { <mat-option [value]="d.id">{{ d.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Team</mat-label>
                <mat-select formControlName="teamId">
                  <mat-option value="">—</mat-option>
                  @for (t of teams; track t.id) { <mat-option [value]="t.id">{{ t.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Designation</mat-label>
                <input matInput formControlName="jobTitle" list="designation-list">
                <datalist id="designation-list">
                  @for (d of designations; track d.id) { <option [value]="d.label"></option> }
                </datalist>
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Reporting manager</mat-label>
                <mat-select formControlName="managerId"><mat-option value="">—</mat-option>
                  @for (m of managers; track m.id) { <mat-option [value]="m.id">{{ m.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Team lead</mat-label>
                <mat-select formControlName="teamLeadId"><mat-option value="">—</mat-option>
                  @for (m of managers; track m.id) { <mat-option [value]="m.id">{{ m.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Project manager</mat-label>
                <mat-select formControlName="projectManagerId"><mat-option value="">—</mat-option>
                  @for (m of managers; track m.id) { <mat-option [value]="m.id">{{ m.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>HR manager</mat-label>
                <mat-select formControlName="hrManagerId"><mat-option value="">—</mat-option>
                  @for (m of managers; track m.id) { <mat-option [value]="m.id">{{ m.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline"><mat-label>Employment type</mat-label>
                <mat-select formControlName="employmentType">
                  @for (t of employmentTypes; track t.value) { <mat-option [value]="t.value">{{ t.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Employment status</mat-label>
                <mat-select formControlName="status">
                  @for (s of statuses; track s.value) { <mat-option [value]="s.value">{{ s.label }}</mat-option> }
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Shift</mat-label><input matInput formControlName="shift"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Office location</mat-label><input matInput formControlName="officeLocation"></mat-form-field>
            </div>

            @if (!isEditMode) {
              <h4>Login credentials</h4>
              <div class="form-row">
                <mat-form-field appearance="outline"><mat-label>Password</mat-label>
                  <input matInput type="password" formControlName="password" placeholder="Auto-generate if blank">
                </mat-form-field>
                <mat-form-field appearance="outline"><mat-label>Portal role</mat-label>
                  <mat-select formControlName="loginRole">
                    @for (r of loginRoles; track r.id) { <mat-option [value]="r.id">{{ r.label }}</mat-option> }
                  </mat-select>
                </mat-form-field>
              </div>
            }
          </div>
        </mat-tab>

        @if (canViewSalary) {
          <mat-tab label="Salary">
            <div class="tab-body">
              <p class="hint">Visible only to authorized HR and Finance users.</p>
              <fieldset [disabled]="!canEditSalary" class="salary-fields">
                <div class="form-row">
                  <mat-form-field appearance="outline"><mat-label>Salary type</mat-label>
                    <mat-select formControlName="salaryType">
                      @for (s of salaryTypes; track s.value) { <mat-option [value]="s.value">{{ s.label }}</mat-option> }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Hourly rate</mat-label><input matInput type="number" formControlName="hourlyRate"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Monthly salary</mat-label><input matInput type="number" formControlName="monthlySalary"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Overtime rate</mat-label><input matInput type="number" formControlName="overtimeRate"></mat-form-field>
                </div>
                <div class="form-row">
                  <mat-form-field appearance="outline"><mat-label>Fuel allowance</mat-label><input matInput type="number" formControlName="fuelAllowance"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Medical allowance</mat-label><input matInput type="number" formControlName="medicalAllowance"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Other allowances</mat-label><input matInput type="number" formControlName="otherAllowances"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Tax number</mat-label><input matInput formControlName="taxNumber"></mat-form-field>
                </div>
                <div class="form-row">
                  <mat-form-field appearance="outline"><mat-label>Bank name</mat-label><input matInput formControlName="bankName"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Account holder</mat-label><input matInput formControlName="bankAccountHolderName"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>Bank account</mat-label><input matInput formControlName="accountNumber"></mat-form-field>
                  <mat-form-field appearance="outline"><mat-label>IBAN</mat-label><input matInput formControlName="iban"></mat-form-field>
                </div>
                <mat-form-field appearance="outline" class="full"><mat-label>Bank branch</mat-label><input matInput formControlName="bankBranch"></mat-form-field>
              </fieldset>
            </div>
          </mat-tab>
        }
      </mat-tab-group>
    </form>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="cancel()" [disabled]="saving">Cancel</button>
  <button mat-raised-button color="primary" type="button" (click)="save()" [disabled]="employeeForm.invalid || saving || loading">
    {{ saving ? 'Saving…' : 'Save' }}
  </button>
</mat-dialog-actions>
